import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Badge } from '../ui/badge';
import { CircleDot, AlertCircle, AlertTriangle, Info, RotateCcw, Calculator, Table2 } from 'lucide-react';
import {
  DEFAULT_INPUTS, FIELD_SPECS, CUSTOM_CRR_SPEC, TYRE_CLASSES, SURFACES, lookupCrr, validateField,
} from '../../lib/rollingResistanceTools';
import { rollingResistanceToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries([...FIELD_SPECS, CUSTOM_CRR_SPEC].map((s) => [s.key, s]));
const MAX_GRADE = SPEC_BY_KEY.gradePercent.max;
// Diagram arrow length saturates here; beyond it the arrow stays at full length.
const DIAGRAM_MAX_CRR = 0.05;

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtSigned = (n, d = 1) => (n === null || n === undefined || Number.isNaN(n) ? '—' : `${n > 0 ? '+' : ''}${fmt(n, d)}`);

const initialRaw = () => {
  const out = {};
  Object.keys(SPEC_BY_KEY).forEach((k) => { out[k] = String(DEFAULT_INPUTS[k]); });
  return out;
};

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" data-testid={`rolling-resistance-tip-${htmlFor}`} />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const NumberField = ({ fieldKey, id, tip, raw, setField, fieldErrors, className = '', children }) => (
  <div className={`space-y-2 ${className}`}>
    <FieldLabel htmlFor={id} unit={SPEC_BY_KEY[fieldKey].unit} tip={tip}>{children}</FieldLabel>
    <Input
      id={id}
      type="number"
      step="any"
      value={raw[fieldKey]}
      onChange={(e) => setField(fieldKey, e.target.value)}
      className="font-mono"
      aria-invalid={!!fieldErrors[fieldKey]}
      data-testid={`rolling-resistance-${fieldKey}-input`}
    />
    {fieldErrors[fieldKey] && (
      <p className="flex items-center gap-1 text-xs text-destructive" data-testid={`rolling-resistance-${fieldKey}-error`}>
        <AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors[fieldKey]}
      </p>
    )}
  </div>
);

const ResultRow = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono font-bold text-sm">{children}</p>
  </div>
);

const Unit = ({ children }) => <span className="text-xs font-normal text-muted-foreground">{children}</span>;

const RollingResistanceCalculator = () => {
  const [tyreClass, setTyreClass] = useState(DEFAULT_INPUTS.tyreClass);
  const [surface, setSurface] = useState(DEFAULT_INPUTS.surface);
  const [raw, setRaw] = useState(initialRaw);
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));
  const isCustom = tyreClass === 'custom';

  // Named tyre classes fill the (locked) Crr field with the lookup value, so switching to
  // Custom starts from the last preset value.
  useEffect(() => {
    const crr = lookupCrr(tyreClass, surface);
    if (crr !== null) setField('rollingResistanceCoefficient', String(Number(crr.toFixed(5))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreClass, surface]);

  const activeSpecs = useMemo(() => [...FIELD_SPECS, ...(isCustom ? [CUSTOM_CRR_SPEC] : [])], [isCustom]);

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const spec of activeSpecs) {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    }
    return { fieldErrors: errors, values: { ...parsed, tyreClass, surface } };
  }, [raw, activeSpecs, tyreClass, surface]);
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  const runCalculation = () => {
    if (hasErrors) return;
    const requestId = ++requestIdRef.current;
    rollingResistanceToolApi.calculate(values)
      .then((outcome) => {
        if (requestId !== requestIdRef.current) return;
        if (outcome.errors) {
          setResults(null);
          setCalcError(Object.values(outcome.errors)[0]);
        } else {
          setResults(outcome.results);
          setCalcError(null);
        }
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setResults(null);
        setCalcError(e.message);
      });
  };

  useEffect(() => {
    if (hasErrors) { setResults(null); setCalcError(null); return; }
    const timer = setTimeout(runCalculation, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, values]);

  const handleReset = () => {
    setRaw(initialRaw());
    setTyreClass(DEFAULT_INPUTS.tyreClass);
    setSurface(DEFAULT_INPUTS.surface);
  };

  const applyMatrixCell = (tyreKey, surfaceKey) => {
    setTyreClass(tyreKey);
    setSurface(surfaceKey);
  };

  // The diagram is driven directly off the raw inputs (not the debounced backend
  // result) so it visibly updates immediately as the user types.
  const liveGradePercent = useMemo(() => {
    const n = Number(raw.gradePercent);
    if (Number.isNaN(n)) return 0;
    return Math.max(-MAX_GRADE, Math.min(MAX_GRADE, n));
  }, [raw.gradePercent]);

  const liveCrr = useMemo(() => {
    const n = Number(raw.rollingResistanceCoefficient);
    if (Number.isNaN(n) || n <= 0) return 0;
    return Math.min(n, DIAGRAM_MAX_CRR);
  }, [raw.rollingResistanceCoefficient]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6" aria-label="Rolling Resistance Force Calculator">
        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="technical-card lg:col-span-3">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <CircleDot className="h-5 w-5 text-autoxium-blue" />
                Rolling Resistance Force Calculator
              </CardTitle>
              <CardDescription>
                Calculate the resistive force between the tyres and the road, and the power and
                energy needed to overcome it. The force depends on the load on the tyres and on
                the tyre/road rolling resistance coefficient (Crr). At normal road speeds it
                changes very little with speed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-md">
                <NumberField fieldKey="vehicleMassKg" id="rolling-resistance-mass" raw={raw} setField={setField} fieldErrors={fieldErrors}
                  tip="Mass actually on the tyres: kerb weight plus driver, passengers and cargo. Homologation road-load tests use test mass (kerb + about 100 kg); for trucks use the gross combination weight.">
                  Vehicle Mass
                </NumberField>
                <NumberField fieldKey="vehicleSpeedKmph" id="rolling-resistance-speed" raw={raw} setField={setField} fieldErrors={fieldErrors}
                  tip="Used for the power output (P = F × v). The force and the energy per km do not depend on speed in this model.">
                  Vehicle Speed
                </NumberField>
                <NumberField fieldKey="gradePercent" id="rolling-resistance-grade" raw={raw} setField={setField} fieldErrors={fieldErrors}
                  tip="Road grade as a percentage. Positive = uphill, negative = downhill, 0 = flat. Only the cos θ reduction of the normal force applies here; the along-slope weight component is a separate force (see the Gradient Force Calculator).">
                  Road Gradient
                </NumberField>
                <NumberField fieldKey="drivetrainEfficiencyPct" id="rolling-resistance-eta" raw={raw} setField={setField} fieldErrors={fieldErrors}
                  tip="Wheel-to-source efficiency (battery or engine output to wheels). Typical: EV 85–92%, ICE manual 88–92%, automatic 80–88%.">
                  Drivetrain Efficiency
                </NumberField>

                <div className="space-y-2">
                  <FieldLabel htmlFor="rolling-resistance-tyre" tip="Tyre class sets the base Crr on smooth dry road. Low-RR / EV tyres are typically EU label A–B; standard passenger C–D. Choose Custom Crr to enter a measured or datasheet value.">
                    Tyre Class
                  </FieldLabel>
                  <Select value={tyreClass} onValueChange={setTyreClass}>
                    <SelectTrigger id="rolling-resistance-tyre" data-testid="rolling-resistance-tyre-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYRE_CLASSES.map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="rolling-resistance-surface" tip="Paved surfaces scale the tyre's base Crr. On loose surfaces (gravel, snow, dirt, sand) the ground's own deformation dominates, so one typical Crr is used for every tyre class.">
                    Road Surface
                  </FieldLabel>
                  <Select value={surface} onValueChange={setSurface} disabled={isCustom}>
                    <SelectTrigger id="rolling-resistance-surface" data-testid="rolling-resistance-surface-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SURFACES.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <FieldLabel htmlFor="rolling-resistance-crr" unit="Crr" tip="Rolling resistance coefficient. Filled from Tyre Class × Road Surface; editable when Tyre Class is Custom Crr. An EU tyre label value in kg/t divided by 1000 gives Crr.">
                    Rolling Resistance Coefficient
                  </FieldLabel>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      id="rolling-resistance-crr"
                      type="number"
                      step="any"
                      value={raw.rollingResistanceCoefficient}
                      onChange={(e) => setField('rollingResistanceCoefficient', e.target.value)}
                      disabled={!isCustom}
                      className="font-mono max-w-[10rem]"
                      aria-invalid={!!fieldErrors.rollingResistanceCoefficient}
                      data-testid="rolling-resistance-crr-input"
                    />
                    <span className="text-xs text-muted-foreground">
                      {isCustom ? `Range ${CUSTOM_CRR_SPEC.min} – ${CUSTOM_CRR_SPEC.max}` : 'From tyre class × surface'}
                    </span>
                  </div>
                  {fieldErrors.rollingResistanceCoefficient && (
                    <p className="flex items-center gap-1 text-xs text-destructive" data-testid="rolling-resistance-crr-error">
                      <AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.rollingResistanceCoefficient}
                    </p>
                  )}
                </div>
              </div>

              <Accordion type="single" collapsible>
                <AccordionItem value="advanced" className="border-none">
                  <AccordionTrigger className="hover:no-underline py-2 font-mono text-xs uppercase tracking-wider text-muted-foreground" data-testid="rolling-resistance-advanced-toggle">
                    Advanced
                  </AccordionTrigger>
                  <AccordionContent>
                    <NumberField fieldKey="gravityMs2" id="rolling-resistance-gravity" raw={raw} setField={setField} fieldErrors={fieldErrors} className="max-w-[12rem] pt-1"
                      tip="Gravitational acceleration. Standard Earth gravity is 9.81 m/s² (9.78 at the equator to 9.83 at the poles).">
                      Gravity
                    </NumberField>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div className="flex gap-2">
                <Button type="button" onClick={runCalculation} disabled={hasErrors} data-testid="rolling-resistance-calculate-btn">
                  <Calculator className="h-4 w-4" /> Calculate
                </Button>
                <Button type="button" variant="outline" onClick={handleReset} data-testid="rolling-resistance-reset-btn">
                  <RotateCcw className="h-4 w-4" /> Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4">
            <ResultsCard hasErrors={hasErrors} calcError={calcError} results={results} />
            {results && results.warnings.length > 0 && <WarningsCard warnings={results.warnings} />}
          </div>
        </div>

        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Vehicle-on-Road Diagram</CardTitle>
            <CardDescription>Updates live as Road Gradient and Crr change. Vector lengths are illustrative, not to scale.</CardDescription>
          </CardHeader>
          <CardContent>
            <RollingResistanceDiagram gradePercent={liveGradePercent} maxGrade={MAX_GRADE} crr={liveCrr} maxCrr={DIAGRAM_MAX_CRR} />
          </CardContent>
        </Card>

        {results && (
          <>
            <CrrMatrixTable results={results} onUse={applyMatrixCell} />
            <SpeedTable results={results} />
            <SensitivityTable results={results} />
            <EuLabelTable results={results} />
          </>
        )}

        <MethodCard results={results} />
      </div>
    </TooltipProvider>
  );
};

const ResultsCard = ({ hasErrors, calcError, results }) => (
  <Card className="technical-card ring-1 ring-autoxium-blue/30 h-fit" data-testid="rolling-resistance-results-card">
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-base">Rolling Resistance</CardTitle>
      <CardDescription>Force, Power &amp; Energy</CardDescription>
    </CardHeader>
    <CardContent>
      <div role="status" aria-live="polite">
        {hasErrors ? (
          <p className="text-sm text-muted-foreground" data-testid="rolling-resistance-results-placeholder">
            Fix the highlighted field to see results.
          </p>
        ) : calcError ? (
          <div className="flex items-center gap-2 text-sm text-destructive" data-testid="rolling-resistance-calc-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
          </div>
        ) : !results ? (
          <p className="text-sm text-muted-foreground" data-testid="rolling-resistance-results-loading">
            Calculating…
          </p>
        ) : (
          <div className="space-y-4" data-testid="rolling-resistance-results">
            <div>
              <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Rolling Resistance Force</p>
              <p className="font-mono font-black text-2xl text-autoxium-blue" data-testid="rolling-resistance-force">{fmt(results.rollingResistanceForceN, 1)} <span className="text-sm font-normal text-muted-foreground">N</span></p>
              <p className="font-mono text-sm text-muted-foreground">
                {fmt(results.rollingResistanceForceKn, 3)} kN · {fmt(results.rollingResistanceForceLbf, 1)} lbf · {fmt(results.rollingResistanceForceKgf, 1)} kgf
              </p>
            </div>
            <div className="pt-3 border-t border-structure grid grid-cols-2 gap-3">
              <ResultRow label="Power at Wheels" testId="rolling-resistance-wheel-power">
                {fmt(results.wheelPowerKw, 2)} <Unit>kW</Unit>
                <span className="block text-xs font-normal text-muted-foreground">{fmt(results.wheelPowerHp, 1)} hp · {fmt(results.inputs.vehicleSpeedKmph, 0)} km/h</span>
              </ResultRow>
              <ResultRow label="Power at Source" testId="rolling-resistance-source-power">
                {fmt(results.sourcePowerKw, 2)} <Unit>kW</Unit>
                <span className="block text-xs font-normal text-muted-foreground">{fmt(results.sourcePowerHp, 1)} hp · η {fmt(results.inputs.drivetrainEfficiencyPct, 0)}%</span>
              </ResultRow>
              <ResultRow label="Energy at Wheels" testId="rolling-resistance-wheel-energy">
                {fmt(results.wheelEnergyWhPerKm, 1)} <Unit>Wh/km</Unit>
              </ResultRow>
              <ResultRow label="Energy at Source" testId="rolling-resistance-source-energy">
                {fmt(results.sourceEnergyWhPerKm, 1)} <Unit>Wh/km</Unit>
              </ResultRow>
            </div>
            <div className="pt-3 border-t border-structure grid grid-cols-2 gap-3">
              <ResultRow label="Crr Used" testId="rolling-resistance-crr-used">
                {fmt(results.crr, 4)}
                <span className="block text-xs font-normal text-muted-foreground">{fmt(results.crrKgPerT, 1)} kg/t (EU label units)</span>
              </ResultRow>
              <ResultRow label="Normal Force" testId="rolling-resistance-normal">
                {fmt(results.normalForceN, 0)} <Unit>N</Unit>
              </ResultRow>
              <ResultRow label="Gradient Angle (θ)" testId="rolling-resistance-angle">
                {fmt(results.roadGradientAngleDeg, 2)} <Unit>°</Unit>
              </ResultRow>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed" data-testid="rolling-resistance-interpretation">
              Acts only while the wheels roll, always opposing the direction of motion. Crr is
              the rated value (warm tyre, correct pressure); see the sensitivity table for how
              pressure, temperature, speed and wet roads move it.
            </p>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const WarningsCard = ({ warnings }) => (
  <Card className="technical-card border-amber-500/40" data-testid="rolling-resistance-warnings">
    <CardContent className="pt-6 space-y-2">
      {warnings.map((w) => (
        <p key={w} className="flex gap-2 text-xs text-muted-foreground leading-relaxed">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" /> <span>{w}</span>
        </p>
      ))}
    </CardContent>
  </Card>
);

const TableCard = ({ title, description, children, testId }) => (
  <Card className="technical-card" data-testid={testId}>
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2">
        <Table2 className="h-4 w-4 text-autoxium-blue" /> {title}
      </CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const HEAD_ROW = 'text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent';
const TH = 'text-right h-auto py-2 pr-3 whitespace-nowrap';
const TD = 'text-right py-2 pr-3 whitespace-nowrap';

const CrrMatrixTable = ({ results, onUse }) => {
  const { tyreClasses, rows } = results.crrMatrix;
  return (
    <TableCard
      title="Crr & Force by Tyre Class and Surface"
      testId="rolling-resistance-crr-table"
      description={`Typical Crr and the resulting force for your vehicle (${fmt(results.inputs.vehicleMassKg, 0)} kg, ${fmt(results.inputs.gradePercent, 1)}% grade). Click a cell to use it. Approximate values after the Bosch Automotive Handbook and Wong; loose surfaces use one Crr for every tyre class.`}
    >
      <Table className="text-xs font-mono min-w-[720px]">
        <TableHeader>
          <TableRow className={HEAD_ROW}>
            <TableHead className="text-left h-auto py-2 pr-3">Surface</TableHead>
            <TableHead className="text-left h-auto py-2 pr-3 whitespace-nowrap">Typical</TableHead>
            {tyreClasses.map((t) => (
              <TableHead key={t.key} className={TH}>{t.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.surface}>
              <TableCell className="py-2 pr-3 whitespace-nowrap">{row.label}</TableCell>
              <TableCell className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{row.range}</TableCell>
              {row.cells.map((c) => (
                <TableCell key={c.tyreClass} className={`${TD} p-0`}>
                  <button
                    type="button"
                    onClick={() => onUse(c.tyreClass, row.surface)}
                    className={`w-full text-right px-2 py-1.5 rounded hover:bg-autoxium-blue/10 ${c.isCurrent ? 'bg-autoxium-blue/10 font-bold ring-1 ring-autoxium-blue/40' : ''}`}
                    data-testid={`rolling-resistance-cell-${c.tyreClass}-${row.surface}`}
                  >
                    {fmt(c.crr, 4)}
                    <span className="block text-muted-foreground font-normal">{fmt(c.forceN, 0)} N</span>
                  </button>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  );
};

const SpeedTable = ({ results }) => (
  <TableCard
    title="Power & Energy vs Speed"
    testId="rolling-resistance-speed-table"
    description="The force (and so the energy per km) stays the same at every speed in this model; the power to overcome it grows in proportion to speed. The highlighted row is your entered speed."
  >
    <Table className="text-xs font-mono min-w-[560px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Speed (km/h)</TableHead>
          <TableHead className={TH}>Wheel Power (kW)</TableHead>
          <TableHead className={TH}>Source Power (kW)</TableHead>
          <TableHead className={TH}>Energy at Source (Wh/km)</TableHead>
          <TableHead className={TH}>Per 100 km (kWh)</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.speedTable.map((r) => (
          <TableRow key={r.speedKmph} className={r.isCurrent ? 'bg-autoxium-blue/5 font-bold' : ''}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{fmt(r.speedKmph, r.speedKmph % 1 ? 1 : 0)}{r.isCurrent ? ' ◂' : ''}</TableCell>
            <TableCell className={TD}>{fmt(r.wheelPowerKw, 2)}</TableCell>
            <TableCell className={TD}>{fmt(r.sourcePowerKw, 2)}</TableCell>
            <TableCell className={TD}>{fmt(r.sourceEnergyWhPerKm, 1)}</TableCell>
            <TableCell className={TD}>{fmt(r.sourceEnergyKwhPer100Km, 2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const SensitivityTable = ({ results }) => (
  <TableCard
    title="What Changes Crr in Practice"
    testId="rolling-resistance-sensitivity-table"
    description="Typical ranges for modern radial tyres, applied to your current force. Use them to judge how far a real-world value may sit from the rated Crr; they are not added to the main result."
  >
    <Table className="text-xs font-mono min-w-[640px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Condition</TableHead>
          <TableHead className={TH}>Change</TableHead>
          <TableHead className={TH}>Force (N)</TableHead>
          <TableHead className="text-left h-auto py-2">Note</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.sensitivityTable.map((r) => (
          <TableRow key={r.factor}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{r.factor}</TableCell>
            <TableCell className={TD}>{fmtSigned(r.changeLoPct, 0)} to {fmtSigned(r.changeHiPct, 0)}%</TableCell>
            <TableCell className={TD}>{fmt(r.forceLoN, 0)} – {fmt(r.forceHiN, 0)}</TableCell>
            <TableCell className="py-2 text-muted-foreground">{r.note}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const EuLabelTable = ({ results }) => (
  <TableCard
    title="EU Tyre Label Rolling-Resistance Classes"
    testId="rolling-resistance-eu-table"
    description="Regulation (EU) 2020/740. Values are in kg/t (N of resistance per kN of load); divide by 1000 for Crr. Read the class off your tyre's label, then enter it as a Custom Crr. Label values are measured per ISO 28580 (80 km/h, 25 °C, rated load and pressure)."
  >
    <Table className="text-xs font-mono min-w-[480px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Class</TableHead>
          <TableHead className={TH}>C1 Passenger</TableHead>
          <TableHead className={TH}>C2 Van / LT</TableHead>
          <TableHead className={TH}>C3 Truck / Bus</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.euLabelClasses.map((r) => (
          <TableRow key={r.grade}>
            <TableCell className="py-2 pr-3 font-bold">{r.grade}</TableCell>
            <TableCell className={TD}>{r.c1}</TableCell>
            <TableCell className={TD}>{r.c2}</TableCell>
            <TableCell className={TD}>{r.c3}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const MethodCard = ({ results }) => (
  <Card className="technical-card" data-testid="rolling-resistance-method">
    <Accordion type="single" collapsible>
      <AccordionItem value="method" className="border-none">
        <CardHeader className="py-0">
          <AccordionTrigger className="hover:no-underline py-4" data-testid="rolling-resistance-method-toggle">
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Calculation Method</CardTitle>
          </AccordionTrigger>
        </CardHeader>
        <AccordionContent>
          <CardContent className="pt-0 space-y-3 text-xs text-muted-foreground leading-relaxed">
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 1</Badge>
              Crr from the lookup: <span className="font-mono text-foreground">Crr = Crr_tyre × surface factor</span> on paved roads, or the surface's own Crr on loose ground (or your Custom value).
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 2</Badge>
              Road gradient angle: <span className="font-mono text-foreground">θ = atan(Grade % / 100)</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 3</Badge>
              Normal force: <span className="font-mono text-foreground">F_normal = m × g × cos(θ)</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 4</Badge>
              Rolling resistance force: <span className="font-mono text-foreground">F_roll = Crr × F_normal</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 5</Badge>
              Power and energy: <span className="font-mono text-foreground">P_wheel = F_roll × v, P_source = P_wheel / η, E = F_roll / 3.6 Wh/km</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">where</Badge>
              m = vehicle mass (kg), g = gravitational acceleration (m/s²), θ = road gradient
              angle, v = vehicle speed (m/s), η = drivetrain efficiency. 1 N of force costs
              exactly 1/3.6 Wh per km travelled.
            </div>
            {results && (
              <div className="pt-1 font-mono text-[11px] bg-muted/50 rounded-md px-3 py-2 break-all">
                θ = atan({fmt(results.inputs.gradePercent, 1)} / 100) = {fmt(results.roadGradientAngleDeg, 2)}°
                &nbsp;·&nbsp; F_normal = {fmt(results.inputs.vehicleMassKg, 0)} × {fmt(results.inputs.gravityMs2, 2)} × cos({fmt(results.roadGradientAngleDeg, 2)}°) = {fmt(results.normalForceN, 1)} N
                &nbsp;·&nbsp; F_roll = {fmt(results.crr, 4)} × {fmt(results.normalForceN, 1)} = {fmt(results.rollingResistanceForceN, 1)} N
                &nbsp;·&nbsp; P_wheel = {fmt(results.rollingResistanceForceN, 1)} × {fmt(results.inputs.vehicleSpeedKmph / 3.6, 2)} = {fmt(results.wheelPowerKw * 1000, 0)} W
                &nbsp;·&nbsp; E = {fmt(results.rollingResistanceForceN, 1)} / 3.6 = {fmt(results.wheelEnergyWhPerKm, 1)} Wh/km
              </div>
            )}
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">note</Badge>
              Crr is treated as constant with speed. That holds well at normal road speeds for
              modern radial tyres; in reality it rises slowly with speed and steeply near the
              tyre's speed limit (SAE J2452 models this with tyre-specific coefficients).
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">note</Badge>
              Crr values are rated figures: warm tyre, correct pressure, ISO 28580 conditions.
              Cold or under-inflated tyres, wet roads and new tread all raise it. The tyre-class
              and surface values are representative, not a specific tyre measurement; prefer a
              datasheet or EU label value when you have one.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">scope</Badge>
              Rolling resistance only. Aerodynamic drag, the along-slope gradient force, bearing
              and brake drag, and driveline losses beyond η are not included; see the Traction
              Power Calculator for total road load.
            </div>
          </CardContent>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </Card>
);

// ---------------------------------------------------------------------------
// Vehicle-on-road diagram (pure SVG, built as a markup string and injected via
// dangerouslySetInnerHTML). This project's dev-only "visual edits" Babel plugin
// (frontend/plugins/visual-edits/babel-metadata-plugin.js) wraps every dynamic JSX
// child of a lowercase element in an HTML <span data-ve-dynamic> — invalid inside the
// SVG namespace, so hand-authored JSX <svg> children (conditional arrows/labels)
// silently fail to render. Building the SVG as a string sidesteps this: every
// interpolated value here is a computed number or a hardcoded label/color string —
// never user-supplied text.
// ---------------------------------------------------------------------------

const polar = (cx, cy, angleRad, r) => ({ x: cx + r * Math.cos(angleRad), y: cy - r * Math.sin(angleRad) });
const n2 = (v) => v.toFixed(2);

const RollingResistanceDiagram = ({ gradePercent, maxGrade, crr, maxCrr }) => {
  const thetaRad = Math.atan(gradePercent / 100);
  const thetaDeg = (thetaRad * 180) / Math.PI;

  const cx = 200;
  const cy = 150;
  const roadHalf = 140;

  // cos(theta) stays positive across the full ±20% grade range, so roadA (angle theta)
  // always sits on the right (forward) side and roadB always sits on the left
  // (backward) side, regardless of uphill/downhill sign — a fixed, well-defined
  // "forward" convention for the Rolling Resistance vector.
  const forwardPoint = polar(cx, cy, thetaRad, roadHalf);
  const backwardPoint = { x: 2 * cx - forwardPoint.x, y: 2 * cy - forwardPoint.y };

  const perpAngle = thetaRad + Math.PI / 2;
  const vehicle = polar(cx, cy, perpAngle, 16);

  const weightEnd = { x: vehicle.x, y: vehicle.y + 60 };

  const normalLen = 46;
  const normalEnd = polar(vehicle.x, vehicle.y, perpAngle, normalLen);

  const crrMag = Math.max(crr, 0) / Math.max(maxCrr, 1e-6);
  const rollLen = 20 + 60 * crrMag;
  const towardBackward = { x: backwardPoint.x - vehicle.x, y: backwardPoint.y - vehicle.y };
  const towardBackwardMag = Math.hypot(towardBackward.x, towardBackward.y) || 1;
  const rollEnd = {
    x: vehicle.x + (towardBackward.x / towardBackwardMag) * rollLen,
    y: vehicle.y + (towardBackward.y / towardBackwardMag) * rollLen,
  };
  const showRollArrow = crr > 0.0001;

  // Angle arc sits on the forward side, opposite the Rolling Resistance vector, so the
  // two never overlap.
  const arcPoint = polar(cx, cy, thetaRad, 44);
  const horizontalPoint = polar(cx, cy, 0, 44);
  const bisectorAngle = thetaRad / 2;
  const arcControl = Math.abs(thetaRad) > 0.001
    ? polar(cx, cy, bisectorAngle, 44 / Math.cos(thetaRad / 2))
    : horizontalPoint;
  const thetaLabelPos = polar(cx, cy, bisectorAngle, 64);

  const groundPoly = `${n2(forwardPoint.x)},${n2(forwardPoint.y)} ${n2(backwardPoint.x)},${n2(backwardPoint.y)} ${n2(backwardPoint.x)},232 ${n2(forwardPoint.x)},232`;

  const arcMarkup = Math.abs(thetaRad) > 0.005
    ? `<path d="M ${n2(horizontalPoint.x)} ${n2(horizontalPoint.y)} Q ${n2(arcControl.x)} ${n2(arcControl.y)} ${n2(arcPoint.x)} ${n2(arcPoint.y)}" fill="none" stroke="#1E40AF" stroke-width="1.5" />
       <text x="${n2(thetaLabelPos.x)}" y="${n2(thetaLabelPos.y)}" text-anchor="middle" font-size="12" font-family="monospace" font-weight="700" fill="#1E40AF">&#952; = ${thetaDeg.toFixed(1)}&#176;</text>`
    : '';

  const forwardLabel = `<text x="${n2(forwardPoint.x)}" y="${n2(forwardPoint.y - 10)}" text-anchor="middle" font-size="10" font-family="monospace" fill="#64748b">FORWARD</text>`;

  const rollMarkup = showRollArrow
    ? `<line x1="${n2(vehicle.x)}" y1="${n2(vehicle.y)}" x2="${n2(rollEnd.x)}" y2="${n2(rollEnd.y)}" stroke="#dc2626" stroke-width="3" marker-end="url(#rr-arrow-roll)" />
       <text x="${n2(rollEnd.x)}" y="${n2(rollEnd.y - 10)}" text-anchor="middle" font-size="11" font-family="monospace" font-weight="700" fill="#dc2626">Rolling Resistance</text>`
    : '';

  const svgMarkup = `
    <svg viewBox="0 0 400 240" class="w-full h-auto" role="img" aria-label="Vehicle on a ${gradePercent.toFixed(1)}% road gradient, angle ${thetaDeg.toFixed(2)} degrees, Crr ${crr.toFixed(3)}">
      <defs>
        <marker id="rr-arrow-weight" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#64748b" /></marker>
        <marker id="rr-arrow-normal" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#1E40AF" /></marker>
        <marker id="rr-arrow-roll" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#dc2626" /></marker>
      </defs>
      <polygon points="${groundPoly}" fill="#94a3b8" fill-opacity="0.15" />
      <line x1="${n2(forwardPoint.x)}" y1="${n2(forwardPoint.y)}" x2="${n2(backwardPoint.x)}" y2="${n2(backwardPoint.y)}" stroke="#475569" stroke-width="5" stroke-linecap="round" />
      <line x1="${cx - roadHalf}" y1="${cy}" x2="${cx + roadHalf}" y2="${cy}" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="4 4" />
      ${arcMarkup}
      ${forwardLabel}
      <g transform="translate(${n2(vehicle.x)} ${n2(vehicle.y)}) rotate(${n2(-thetaDeg)})">
        <rect x="-6" y="-20" width="20" height="9" rx="2" fill="#1E40AF" />
        <rect x="-23" y="-11" width="46" height="14" rx="4" fill="#1E40AF" />
        <circle cx="-13" cy="3" r="6" fill="#1e293b" />
        <circle cx="13" cy="3" r="6" fill="#1e293b" />
      </g>
      <line x1="${n2(vehicle.x)}" y1="${n2(vehicle.y)}" x2="${n2(weightEnd.x)}" y2="${n2(weightEnd.y)}" stroke="#64748b" stroke-width="2.5" marker-end="url(#rr-arrow-weight)" />
      <text x="${n2(weightEnd.x + 8)}" y="${n2(weightEnd.y + 2)}" font-size="11" font-family="monospace" fill="#64748b">Weight (mg)</text>
      <line x1="${n2(vehicle.x)}" y1="${n2(vehicle.y)}" x2="${n2(normalEnd.x)}" y2="${n2(normalEnd.y)}" stroke="#1E40AF" stroke-width="2.5" marker-end="url(#rr-arrow-normal)" />
      <text x="${n2(normalEnd.x + 8)}" y="${n2(normalEnd.y - 4)}" font-size="11" font-family="monospace" fill="#1E40AF">Normal Force</text>
      ${rollMarkup}
    </svg>
  `;

  return (
    <div
      className="w-full max-w-xl mx-auto"
      data-testid="rolling-resistance-diagram"
      dangerouslySetInnerHTML={{ __html: svgMarkup }}
    />
  );
};

export default RollingResistanceCalculator;
